import {
  applyReceiptDiscount,
  createReceiptRefund,
  createReceiptReturn,
  fetchReceiptFull,
  fetchReceiptPayments,
  processReceiptPayment,
  sendReceiptPdf,
} from "../../services/receiptApi";


/**
 * Retail POS Receipt Runtime Adapter.
 *
 * Stateless orchestration boundary between RetailPOSRuntime
 * and the authoritative Receipt API.
 *
 * This adapter does not own:
 *
 * - Receipt state;
 * - payment state;
 * - discount calculation;
 * - tax calculation;
 * - persistence;
 * - company scope;
 * - activity scope.
 */
export function createRetailPOSReceiptRuntime({
  receiptApi = null,
} = {}) {
  const api =
    receiptApi
    ?? {
      applyReceiptDiscount,
      createReceiptRefund,
      createReceiptReturn,
      fetchReceiptFull,
      fetchReceiptPayments,
      processReceiptPayment,
      sendReceiptPdf,
    };

  assertReceiptApi(api);

  return Object.freeze({
    async resolveReceipt({
      receiptUuid,
      companyUuid,
    } = {}) {
      const scope =
        normalizeReceiptScope({
          receiptUuid,
          companyUuid,
        });

      return api.fetchReceiptFull(
        scope.receiptUuid,
        {
          companyUuid:
            scope.companyUuid,
        }
      );
    },


    async applyDiscount({
      receiptUuid,
      companyUuid,
      payload = {},
    } = {}) {
      const scope =
        normalizeReceiptScope({
          receiptUuid,
          companyUuid,
        });

      const response =
        await api.applyReceiptDiscount(
          scope.receiptUuid,
          payload,
          {
            companyUuid:
              scope.companyUuid,
          }
        );

      return unwrapResponseData(
        response
      );
    },


    async listPayments({
      receiptUuid,
      companyUuid,
    } = {}) {
      const scope =
        normalizeReceiptScope({
          receiptUuid,
          companyUuid,
        });

      const result =
        await api.fetchReceiptPayments(
          scope.receiptUuid,
          {
            companyUuid:
              scope.companyUuid,
          }
        );

      if (Array.isArray(result)) {
        return result;
      }

      if (
        result
        && Array.isArray(
          result.payments
        )
      ) {
        return result.payments;
      }

      return [];
    },


    async submitPayment({
      receiptUuid,
      companyUuid,
      meansPayment,
      amount,
      checkFormat = true,
    } = {}) {
      const scope =
        normalizeReceiptScope({
          receiptUuid,
          companyUuid,
        });

      const normalizedMeansPayment =
        normalizeRequiredString(
          meansPayment,
          "meansPayment"
        );

      const normalizedAmount =
        normalizePositiveAmount(
          amount
        );

      const response =
        await api.processReceiptPayment(
          scope.receiptUuid,
          normalizedMeansPayment,
          normalizedAmount,
          Boolean(checkFormat),
          {
            companyUuid:
              scope.companyUuid,
          }
        );

      return unwrapResponseData(
        response
      );
    },


    async returnProduct(
      payload,
      context = {}
    ) {
      const receiptUuid =
        normalizeReceiptReference(
          payload
        );

      const companyUuid =
        normalizeRequiredString(
          context?.companyUuid,
          "companyUuid"
        );

      const intent =
        normalizeReturnIntent(
          payload
        );

      return api.createReceiptReturn(
        receiptUuid,
        intent,
        {
          companyUuid,
        }
      );
    },


    async refund(
      payload,
      context = {}
    ) {
      const receiptUuid =
        normalizeReceiptReference(
          payload
        );

      const companyUuid =
        normalizeRequiredString(
          context?.companyUuid,
          "companyUuid"
        );

      const intent =
        normalizeRefundIntent(
          payload
        );

      return api.createReceiptRefund(
        receiptUuid,
        intent,
        {
          companyUuid,
        }
      );
    },


    async printReceipt(
      receipt,
      context = {}
    ) {
      const receiptUuid =
        normalizeReceiptReference(
          receipt
        );

      const companyUuid =
        normalizeRequiredString(
          context?.companyUuid,
          "companyUuid"
        );

      const format =
        normalizeOptionalFormat(
          context?.format
          ?? context?.pdfFormat
          ?? "A4"
        );

      const response =
        await api.sendReceiptPdf(
          receiptUuid,
          format,
          {
            companyUuid,
          }
        );

      return unwrapResponseData(
        response
      );
    },
  });
}


export function isRetailPOSReceiptRuntime(
  runtime
) {
  return Boolean(
    runtime
    && typeof runtime === "object"
    && typeof runtime.resolveReceipt
      === "function"
    && typeof runtime.applyDiscount
      === "function"
    && typeof runtime.listPayments
      === "function"
    && typeof runtime.submitPayment
      === "function"
    && typeof runtime.printReceipt
      === "function"
    && typeof runtime.returnProduct
      === "function"
    && typeof runtime.refund
      === "function"
  );
}


function normalizeReceiptReference(
  receipt
) {
  const receiptUuid =
    typeof receipt === "string"
      ? receipt
      : (
          receipt?.receiptUuid
          ?? receipt?.receipt_uuid
          ?? receipt?.uuid
          ?? receipt?.receipt?.uuid
        );

  return normalizeRequiredString(
    receiptUuid,
    "receiptUuid"
  );
}


function normalizeIdempotencyKey(
  payload
) {
  return normalizeRequiredString(
    payload?.idempotencyKey
    ?? payload?.idempotency_key,
    "idempotencyKey"
  );
}


function normalizeReturnIntent(
  payload
) {
  const source =
    payload
    && typeof payload === "object"
      ? payload
      : {};

  const lines =
    Array.isArray(
      source.lines
    )
      ? source.lines.map(
          (line) => ({
            sales_line_uuid:
              line?.salesLineUuid
              ?? line?.sales_line_uuid
              ?? line?.originalSalesLineUuid
              ?? line?.original_sales_line_uuid,

            quantity:
              line?.quantity,

            restock_disposition:
              line?.restockDisposition
              ?? line?.restock_disposition
              ?? "restock",

            reason:
              line?.reason
              ?? null,
          })
        )
      : source.lines;

  return {
    idempotency_key:
      normalizeIdempotencyKey(
        source
      ),

    reason:
      source.reason
      ?? null,

    lines,
  };
}


function normalizeRefundIntent(
  payload
) {
  const source =
    payload
    && typeof payload === "object"
      ? payload
      : {};

  return {
    idempotency_key:
      normalizeIdempotencyKey(
        source
      ),

    amount:
      source.amount,

    means_payment:
      source.meansPayment
      ?? source.means_payment,

    sales_return_uuid:
      source.salesReturnUuid
      ?? source.sales_return_uuid
      ?? null,

    reason:
      source.reason
      ?? null,
  };
}


function normalizeOptionalFormat(
  value
) {
  if (
    typeof value !== "string"
    || value.trim() === ""
  ) {
    return "A4";
  }

  return value.trim();
}


function normalizeReceiptScope({
  receiptUuid,
  companyUuid,
}) {
  return Object.freeze({
    receiptUuid:
      normalizeRequiredString(
        receiptUuid,
        "receiptUuid"
      ),

    companyUuid:
      normalizeRequiredString(
        companyUuid,
        "companyUuid"
      ),
  });
}


function normalizeRequiredString(
  value,
  fieldName
) {
  if (
    typeof value !== "string"
    || value.trim() === ""
  ) {
    throw new TypeError(
      `${fieldName} must be a non-empty string.`
    );
  }

  return value.trim();
}


function normalizePositiveAmount(
  value
) {
  const amount =
    Number(value);

  if (
    !Number.isFinite(amount)
    || amount <= 0
  ) {
    throw new TypeError(
      "amount must be a positive number."
    );
  }

  return amount;
}


function unwrapResponseData(
  response
) {
  if (
    response
    && typeof response === "object"
    && Object.prototype.hasOwnProperty.call(
      response,
      "data"
    )
  ) {
    return response.data;
  }

  return response;
}


function assertReceiptApi(
  api
) {
  if (
    api === null
    || typeof api !== "object"
  ) {
    throw new TypeError(
      "receiptApi must be an object."
    );
  }

  [
    "fetchReceiptFull",
    "applyReceiptDiscount",
    "fetchReceiptPayments",
    "processReceiptPayment",
    "sendReceiptPdf",
    "createReceiptReturn",
    "createReceiptRefund",
  ].forEach(
    (methodName) => {
      if (
        typeof api[methodName]
          !== "function"
      ) {
        throw new TypeError(
          `receiptApi must expose ${methodName}().`
        );
      }
    }
  );
}
